/**
 * The water the boat is about to be in.
 *
 * `predict.ts` answers "where will I be"; this answers "what am I heading at".
 * It is the same dead-reckoning assumption — course and speed held — widened
 * into a sector, because a boat does not hold a course to the degree and a
 * danger a hundred metres off the predicted line is still a danger the skipper
 * is about to meet.
 *
 * The sector is deliberately narrow. Thirty degrees is about what a small craft
 * wanders through in a seaway while nominally steering straight, and widening
 * it further would sweep in the whole coastline on a harbour approach — which is
 * the failure mode the always-on isolated-danger symbol had. The point of this
 * module is to name a few specific things ahead, not to shade a region.
 *
 * This file is pure geometry and knows nothing about the chart. Deciding which
 * charted objects fall inside the sector needs the loaded tiles, so it lives in
 * `src/chart/forwardDangers.ts`.
 */

import {
  type LatLon,
  boundsOf,
  destinationPoint,
  greatCircleBearing,
  greatCircleDistance,
  normaliseBearing,
} from './geo';
import {
  DEFAULT_PREDICTION_HORIZONS_S,
  type PredictionInput,
  canPredict,
} from './predict';

/** Full width of the sector searched ahead, degrees. Half either side of course. */
export const FORWARD_CONE_ANGLE_DEG = 30;

/**
 * Shortest distance the sector ever reaches, metres.
 *
 * At two knots a five-minute horizon is only 300 m, and creeping into an
 * anchorage is exactly when a rock just ahead matters most. The floor keeps the
 * search from collapsing to nothing as the boat slows. It is not drawn — the
 * predicted track keeps its honest length — it only bounds what is looked at.
 */
export const FORWARD_MIN_RANGE_M = 200;

/**
 * Sector length when the skipper has turned every prediction horizon off.
 *
 * Turning the track off is a display choice; it is not a statement that the
 * water ahead stopped mattering. Five minutes is the middle default horizon.
 */
export const DEFAULT_FORWARD_HORIZON_S = 300;

export interface ForwardCone {
  origin: LatLon;
  /** Centreline, degrees true. */
  courseDeg: number;
  /** How far ahead the sector reaches, metres. */
  rangeM: number;
  /** Full width, degrees. */
  angleDeg: number;
  /** Speed the range was derived from, metres per second. */
  speedMs: number;
}

/**
 * The sector ahead of a fix, or null when there is nothing to project.
 *
 * Null under exactly the same conditions as the predicted track: below about a
 * knot the reported course is a bearing between two nearly identical positions,
 * and a sector drawn from it would point confidently at whatever the noise
 * chose. The two must agree or the chart would mark dangers ahead of a boat it
 * is simultaneously refusing to draw a heading for.
 */
export function forwardCone(
  input: PredictionInput,
  horizonsS: readonly number[] = DEFAULT_PREDICTION_HORIZONS_S,
  angleDeg: number = FORWARD_CONE_ANGLE_DEG,
): ForwardCone | null {
  if (!canPredict(input)) return null;

  const speedMs = input.speedMs as number;
  const horizons = horizonsS.filter((seconds) => Number.isFinite(seconds) && seconds > 0);
  const horizonS = horizons.length > 0 ? Math.max(...horizons) : DEFAULT_FORWARD_HORIZON_S;

  return {
    origin: { lat: input.position.lat, lon: input.position.lon },
    courseDeg: normaliseBearing(input.courseDeg as number),
    rangeM: Math.max(FORWARD_MIN_RANGE_M, speedMs * horizonS),
    angleDeg,
    speedMs,
  };
}

/**
 * Signed difference from the course to a bearing, −180..180.
 * Negative is to port, positive to starboard — which is how a helmsman reads it.
 */
export function bearingOffset(courseDeg: number, bearingDeg: number): number {
  const delta = normaliseBearing(bearingDeg - courseDeg);
  return delta > 180 ? delta - 360 : delta;
}

export interface ConeHit {
  distanceM: number;
  /** Signed offset from the centreline, degrees; negative to port. */
  offsetDeg: number;
  /** Seconds to reach it at the current speed, or null if not moving usefully. */
  timeToS: number | null;
}

/**
 * Where a point sits relative to the sector, or null if it is outside.
 *
 * A point at the origin returns null rather than a zero-distance hit: the boat
 * cannot be warned about the water it is already in, and a bearing to a point
 * zero metres away is meaningless.
 */
export function coneHit(cone: ForwardCone, point: LatLon): ConeHit | null {
  const distanceM = greatCircleDistance(cone.origin, point);
  if (distanceM <= 0 || distanceM > cone.rangeM) return null;

  const offsetDeg = bearingOffset(cone.courseDeg, greatCircleBearing(cone.origin, point));
  if (Math.abs(offsetDeg) > cone.angleDeg / 2) return null;

  return {
    distanceM,
    offsetDeg,
    timeToS: cone.speedMs > 0 ? distanceM / cone.speedMs : null,
  };
}

/** True when the point lies inside the sector. */
export function coneContains(cone: ForwardCone, point: LatLon): boolean {
  return coneHit(cone, point) !== null;
}

/**
 * The sector as a closed ring, origin first.
 *
 * Not drawn — the user asked for the dangers, not the sector, and a shaded wedge
 * over the chart is one more thing covering the depth shading. It exists so the
 * caller can take a bounding box, and so the geometry is testable as a shape
 * rather than only as a predicate.
 */
export function conePolygon(cone: ForwardCone, steps = 8): LatLon[] {
  const half = cone.angleDeg / 2;
  const ring: LatLon[] = [cone.origin];
  for (let i = 0; i <= steps; i++) {
    const bearing = cone.courseDeg - half + (cone.angleDeg * i) / steps;
    ring.push(destinationPoint(cone.origin, bearing, cone.rangeM));
  }
  ring.push(cone.origin);
  return ring;
}

export interface ConeRing {
  /** How far ahead the middle of this slice is, metres. */
  distanceM: number;
  /** Where the course line crosses it. */
  position: LatLon;
  /**
   * Radius about `position` that covers this whole slice of the sector.
   *
   * Two things at once, and it is the larger of them:
   *
   * - **Across the sector.** The arc at distance d has its ends a chord of
   *   2·d·sin(θ/4) from the centreline, so a disc of that radius round the
   *   centreline point contains the entire arc.
   * - **Along the course.** At least half the gap to the neighbouring rings, so
   *   consecutive discs touch and no band of water between two of them is
   *   skipped.
   *
   * The second clause is the one that was missing, and it is why a rock could be
   * in the sector and never sampled. See `coneRings`.
   */
  radiusM: number;
}

/**
 * Slices across the sector, nearest first.
 *
 * The three-ray version of this missed the thing it existed to find. Rays
 * diverge: at a minute ahead at 15 knots they are 120 m apart, and at the far
 * end of a five-minute cone, 600 m. A shoal sitting between two rays was simply
 * not sampled, and the absence of a mark read as clear water. Rings are sampled
 * as areas instead — see `coneArc` for pinning down where inside a slice the
 * shoal actually is.
 *
 * **The rings must cover the course line without gaps, and the first version of
 * this did not.** It took a fixed step of `max(spacingM, rangeM / maxRings)` and
 * gave each ring a disc the width of the arc — which grows with distance while
 * the step does not. Two consequences, both of them observed on the water at
 * Halifax with a boat doing 20 knots:
 *
 * - the first ring sat at `rangeM / maxRings` — **96 m ahead**, so a rock closer
 *   than about 71 m was never sampled at all;
 * - the discs were narrower than the step until roughly 190 m, leaving a blind
 *   band at 122–143 m.
 *
 * A hazard drifted through those as the boat closed, so it was marked, then not,
 * then marked again — "it depends where exactly I put the boat", which is
 * precisely what a scan must never depend on.
 *
 * The rule now is that **the disc is never smaller than half the step**, so the
 * slices are contiguous by construction whatever the angle or the range, and the
 * step grows with the arc so a long cone does not cost a query every 50 m to the
 * horizon. Starting distance is near the boat rather than a fraction of the
 * range, because the water just ahead is the water there is least time to do
 * anything about.
 */
export function coneRings(
  cone: ForwardCone,
  spacingM: number,
  maxRings = 64,
): ConeRing[] {
  const chordFactor = 2 * Math.sin((cone.angleDeg / 4) * (Math.PI / 180));
  const rings: ConeRing[] = [];

  let distanceM = Math.min(spacingM / 2, cone.rangeM);
  while (distanceM <= cone.rangeM && rings.length < maxRings) {
    const arcHalfWidthM = distanceM * chordFactor;
    // Step by the arc's own reach once that exceeds the probe spacing, so a long
    // cone is covered in a bounded number of queries instead of one every 50 m.
    const stepM = Math.max(spacingM, arcHalfWidthM);
    rings.push({
      distanceM,
      position: destinationPoint(cone.origin, cone.courseDeg, distanceM),
      // Half the step is the floor: whatever the geometry, consecutive discs
      // meet. Over-covering across the sector is harmless — `coneArc` then
      // decides whether the hit is really inside it.
      radiusM: Math.max(arcHalfWidthM, stepM / 2),
    });
    distanceM += stepM;
  }
  return rings;
}

export interface ConeArcPoint {
  position: LatLon;
  distanceM: number;
  /** Signed offset from the course, degrees; negative to port. */
  offsetDeg: number;
}

/**
 * Points across the sector at one distance, ordered by how far they are off the
 * course — dead ahead first.
 *
 * Used to confirm and place what a ring query found. Ordering matters: of
 * several shoal samples at the same range, the one nearest the course line is
 * the one the boat is actually heading at, and that is where the mark belongs.
 *
 * The sample count follows the arc length so the spacing on the ground stays
 * roughly constant however far ahead the ring is. Capped, because this runs
 * after a hit and there is no value in resolving a shoal to the metre.
 */
export function coneArc(
  cone: ForwardCone,
  distanceM: number,
  spacingM: number,
  maxSamples = 25,
): ConeArcPoint[] {
  const half = cone.angleDeg / 2;
  const arcWidthM = 2 * distanceM * Math.sin((half) * (Math.PI / 180));
  const samples = Math.min(maxSamples, Math.max(3, Math.ceil(arcWidthM / spacingM) + 1));

  const points: ConeArcPoint[] = [];
  for (let i = 0; i < samples; i++) {
    const offsetDeg = -half + (2 * half * i) / (samples - 1);
    points.push({
      offsetDeg,
      distanceM,
      position: destinationPoint(cone.origin, cone.courseDeg + offsetDeg, distanceM),
    });
  }

  return points.sort((a, b) => Math.abs(a.offsetDeg) - Math.abs(b.offsetDeg));
}

/**
 * Every point worth probing inside one ring's slice, nearest first.
 *
 * `coneArc` walks one radius. A disc covers `distanceM ± radiusM`, so
 * confirming a hit at the ring's own radius alone can find nothing and drop a
 * shoal the disc genuinely saw — the square is fat along the course and the arc
 * is a hairline across it. That was the second half of the Halifax gap.
 *
 * Ordered nearest first, and dead-ahead first within each radius: of several
 * shoal samples the one you meet soonest is the one to name, and of several at
 * the same range the one on the course line is the one being steered at.
 */
export function coneSlice(
  cone: ForwardCone,
  ring: ConeRing,
  spacingM: number,
): ConeArcPoint[] {
  // Down to a quarter of the spacing, not half: the water immediately ahead is
  // the water there is least time to do anything about, and a sliver of unsafe
  // ground twenty metres off the bow was being clamped out of the walk.
  const from = Math.max(spacingM / 4, ring.distanceM - ring.radiusM);
  const to = Math.min(cone.rangeM, ring.distanceM + ring.radiusM);

  /**
   * Radial step across the slice.
   *
   * Never coarser than the probe spacing, and finer on a thick slice — half its
   * radius. The mark is placed at the first radius that qualifies, so this is
   * the error in *where the danger is said to be*, and saying it is further off
   * than it is fails in the wrong direction. Bounded at about five radii a
   * slice, and only walked when a square has already found something.
   */
  const stepM = Math.min(spacingM, Math.max(ring.radiusM / 2, spacingM / 4));
  const points: ConeArcPoint[] = [];

  for (let distanceM = from; distanceM <= to + 1e-9; distanceM += stepM) {
    points.push(...coneArc(cone, distanceM, spacingM));
  }
  // The slice can be shorter than the spacing; still probe its middle.
  if (points.length === 0) points.push(...coneArc(cone, ring.distanceM, spacingM));

  return points.sort((a, b) => a.distanceM - b.distanceM
    || Math.abs(a.offsetDeg) - Math.abs(b.offsetDeg));
}

/** Bounding box of the sector — what to ask the map for before filtering. */
export function coneBounds(
  cone: ForwardCone,
): { south: number; west: number; north: number; east: number } {
  // conePolygon always returns points, so boundsOf cannot be null here.
  return boundsOf(conePolygon(cone)) as { south: number; west: number; north: number; east: number };
}
